import { toast } from 'sonner';
import { Plug, RefreshCw, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { trpc } from '../lib/trpc';
import { translateError } from '../lib/errors';
import { Badge, Button, Card, CardContent, EmptyState, PageSpinner } from '../components/ui';
import { useLocale } from '../contexts/LocaleContext';

const typeIcon: Record<string, React.ReactNode> = {
  facebook: <Plug className="h-4 w-4" />,
  webhook: <Plug className="h-4 w-4" />,
};
const statusBadgeVariant = (s: string): 'success' | 'secondary' | 'destructive' =>
  s === 'active' ? 'success' : s === 'unverified' ? 'secondary' : 'destructive';

export function ConnectionsPage() {
  const { t, relativeTime } = useLocale();
  const utils = trpc.useUtils();
  const list = trpc.connections.list.useQuery();
  const verify = trpc.connections.verify.useMutation({
    onSuccess: () => {
      toast.success(t('connections.toastVerified'));
      void utils.connections.invalidate();
    },
    onError: (e) => toast.error(translateError(e)),
  });
  const disconnect = trpc.connections.disconnect.useMutation({
    onSuccess: () => {
      toast.success(t('connections.toastRemoved'));
      void utils.connections.invalidate();
    },
  });
  const rename = trpc.connections.rename.useMutation({
    onSuccess: () => {
      void utils.connections.invalidate();
    },
  });

  const connectFacebook = () => {
    window.location.href = '/api/auth/facebook/start';
  };

  const statusLabel = (s: string) =>
    s === 'active'
      ? t('connections.statusActive')
      : s === 'unverified'
        ? t('connections.statusUnverified')
        : t('connections.statusError');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('connections.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('connections.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={connectFacebook}>
            <Plug className="h-3.5 w-3.5" /> {t('connections.connectFacebook')}
          </Button>
        </div>
      </div>

      {list.isLoading ? (
        <PageSpinner />
      ) : !list.data?.length ? (
        <EmptyState
          title={t('connections.emptyTitle')}
          description={t('connections.emptyDescription')}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.data.map((c) => (
            <Card key={c.id}>
              <CardContent className="space-y-3 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                      {typeIcon[c.type] ?? <Plug className="h-4 w-4" />}
                    </div>
                    <div>
                      <button
                        className="text-sm font-semibold hover:underline"
                        onClick={() => {
                          const name = prompt(t('connections.renamePrompt'), c.name);
                          if (name) rename.mutate({ id: c.id, name });
                        }}
                      >
                        {c.name}
                      </button>
                      <p className="text-xs capitalize text-muted-foreground">
                        {c.type}
                        {c.externalId ? ` · ${c.externalId}` : ''}
                      </p>
                    </div>
                  </div>
                  <Badge variant={statusBadgeVariant(c.status)}>{statusLabel(c.status)}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {c.type === 'facebook' && t('connections.pageCount', { count: c.pageCount })}
                  {c.type !== 'facebook' && ''}
                  {c.lastVerifiedAt
                    ? ` · ${t('connections.verifiedAgo', { when: relativeTime(c.lastVerifiedAt) })}`
                    : ` · ${t('connections.neverVerified')}`}
                  {c.lastError ? <span className="text-destructive"> · {c.lastError}</span> : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => verify.mutate({ id: c.id })}
                    disabled={verify.isPending}
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> {t('connections.verify')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => {
                      if (confirm(t('connections.removeConfirm'))) disconnect.mutate({ id: c.id });
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t('connections.remove')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FacebookPagesPanel />
    </div>
  );
}

function FacebookPagesPanel() {
  const { t } = useLocale();
  const utils = trpc.useUtils();
  const accts = trpc.facebookAccounts.getAccountsWithPages.useQuery();
  const subscribe = trpc.facebookAccounts.subscribePage.useMutation({
    onSuccess: () => {
      toast.success(t('connections.toastPageSubscribed'));
      void utils.facebookAccounts.invalidate();
    },
    onError: (e) => toast.error(translateError(e)),
  });
  const toggle = trpc.facebookAccounts.togglePageActive.useMutation({
    onSuccess: () => void utils.facebookAccounts.invalidate(),
  });
  if (!accts.data?.length) return null;
  void AlertTriangle;
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-1 flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-4 w-4 text-primary" /> {t('connections.pagesTitle')}
        </h3>
        <p className="mb-4 text-xs text-muted-foreground">{t('connections.pagesDescription')}</p>
        <div className="space-y-4">
          {accts.data.map((a) => (
            <div key={a.id}>
              <p className="text-sm font-medium">{a.name}</p>
              <div className="mt-1 space-y-1">
                {a.pages.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('connections.noPagesAccount')}</p>
                )}
                {a.pages.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span>
                      {p.name} <span className="text-xs text-muted-foreground">({p.pageId})</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {p.subscribed ? (
                        <>
                          <Badge variant="success">{t('connections.subscribed')}</Badge>
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={p.active}
                              onChange={(e) => toggle.mutate({ pageId: p.id, active: e.target.checked })}
                            />{' '}
                            {t('connections.active')}
                          </label>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => subscribe.mutate({ pageId: p.id })}
                          disabled={subscribe.isPending}
                        >
                          {t('connections.subscribe')}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
